import type { Deal } from '@/models/registry';
import type {
  DeliveryStage,
  FinanceManager,
  FundingMethod,
  Lender,
  Priority,
  RapidReconStep,
  SalesRep,
  Status,
} from '@/models/types';
import type { Dispatch, SetStateAction } from 'react';

export type StatusFilters = Record<Status, boolean>;

export type DeliveryStageFilters = Record<DeliveryStage, boolean>;

export type RapidReconStepFilters = Record<RapidReconStep, boolean>;

export type DeliveryPreferenceFilters = Record<
  Deal['delivery_preference'],
  boolean
>;

export type SalesRepFilters = Record<SalesRep['id'], boolean>;

export type SalesRepFiltersState = {
  selectedFilters: SalesRepFilters;
  filterMetaState: FilterMetaState & {
    type: 'sales_rep';
  };
};

export type FilterMetaState = {
  type: FilterType;
  position: number;
  isNegated: boolean;
  isCurrentlyBeingBuilt: boolean;
};

export type FilterType =
  | 'status'
  | 'delivery_stage'
  | 'delivery_preference'
  | 'priority'
  | 'finance_manager'
  | 'sales_rep'
  | 'funding_method'
  | 'lender'
  | 'rapid_recon_step'
  | 'delivery_stage'
  | 'delivery_queued'
  | 'price_locked'
  | 'traded_in'
  | 'delivery_date'
  | 'pickup_date'
  | 'created_date'
  | 'last_updated_date';

export const FILTER_TYPES: FilterType[] = [
  'status',
  'delivery_stage',
  'delivery_preference',
  'priority',
  'finance_manager',
  'sales_rep',
  'funding_method',
  'lender',
  'rapid_recon_step',
  'delivery_queued',
  'price_locked',
  'traded_in',
  'delivery_date',
  'pickup_date',
  'created_date',
  'last_updated_date',
];

export type StatusFiltersState = {
  selectedFilters: StatusFilters;
  filterMetaState: FilterMetaState & {
    type: 'status';
  };
};

export type DeliveryStageFiltersState = {
  selectedFilters: DeliveryStageFilters;
  filterMetaState: FilterMetaState & {
    type: 'delivery_stage';
  };
};

export type RapidReconStepFiltersState = {
  selectedFilters: RapidReconStepFilters;
  filterMetaState: FilterMetaState & {
    type: 'rapid_recon_step';
  };
};

export type PriorityFilters = Record<Priority, boolean>;

export type PriorityFiltersState = {
  selectedFilters: PriorityFilters;
  filterMetaState: FilterMetaState & {
    type: 'priority';
  };
};

export type FinanceManagerFilters = Record<FinanceManager['id'], boolean>;

export type FinanceManagerFiltersState = {
  selectedFilters: FinanceManagerFilters;
  filterMetaState: FilterMetaState & {
    type: 'finance_manager';
  };
};

export type FundingMethodFilters = Record<FundingMethod, boolean>;

export type FundingMethodFiltersState = {
  selectedFilters: FundingMethodFilters;
  filterMetaState: FilterMetaState & {
    type: 'funding_method';
  };
};

export type FilterState =
  | StatusFiltersState
  | DeliveryStageFiltersState
  | DeliveryPreferenceFiltersState
  | PriorityFiltersState
  | FinanceManagerFiltersState
  | SalesRepFiltersState
  | FundingMethodFiltersState
  | LenderFiltersState
  | DeliveryQueuedFiltersState
  | PriceLockedFiltersState
  | TradeInFiltersState
  | RapidReconStepFiltersState
  | DeliveryDateFiltersState
  | PickupDateFiltersState
  | LastUpdatedDateFiltersState
  | CreatedDateFiltersState;

export type DateFilterState = DeliveryDateFiltersState;

export type ComboBoxState = 'filter' | FilterType;

export type StatusFilterChipProps = {
  filtersState: StatusFiltersState;
  setFiltersState: Dispatch<SetStateAction<StatusFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type DeliveryStageFilterChipProps = {
  filtersState: DeliveryStageFiltersState;
  setFiltersState: Dispatch<SetStateAction<DeliveryStageFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type RapidReconStepFilterChipProps = {
  filtersState: RapidReconStepFiltersState;
  setFiltersState: Dispatch<SetStateAction<RapidReconStepFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type DeliveryPreferenceFiltersState = {
  selectedFilters: DeliveryPreferenceFilters;
  filterMetaState: FilterMetaState & {
    type: 'delivery_preference';
  };
};

export type DeliveryPreferenceFilterChipProps = {
  filtersState: DeliveryPreferenceFiltersState;
  setFiltersState: Dispatch<SetStateAction<DeliveryPreferenceFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type PriorityFilterChipProps = {
  filtersState: PriorityFiltersState;
  setFiltersState: Dispatch<SetStateAction<PriorityFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type FinanceManagerFilterChipProps = {
  filtersState: FinanceManagerFiltersState;
  setFiltersState: Dispatch<SetStateAction<FinanceManagerFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type FundingMethodFilterChipProps = {
  filtersState: FundingMethodFiltersState;
  setFiltersState: Dispatch<SetStateAction<FundingMethodFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type FilterChipProps =
  | {
      type: 'status';
      filtersState: StatusFiltersState;
      setFiltersState: Dispatch<SetStateAction<StatusFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'delivery_stage';
      filtersState: DeliveryStageFiltersState;
      setFiltersState: Dispatch<SetStateAction<DeliveryStageFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'rapid_recon_step';
      filtersState: RapidReconStepFiltersState;
      setFiltersState: Dispatch<SetStateAction<RapidReconStepFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'delivery_preference';
      filtersState: DeliveryPreferenceFiltersState;
      setFiltersState: Dispatch<SetStateAction<DeliveryPreferenceFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'priority';
      filtersState: PriorityFiltersState;
      setFiltersState: Dispatch<SetStateAction<PriorityFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'finance_manager';
      filtersState: FinanceManagerFiltersState;
      setFiltersState: Dispatch<SetStateAction<FinanceManagerFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'sales_rep';
      filtersState: SalesRepFiltersState;
      setFiltersState: Dispatch<SetStateAction<SalesRepFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'funding_method';
      filtersState: FundingMethodFiltersState;
      setFiltersState: Dispatch<SetStateAction<FundingMethodFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'lender';
      filtersState: LenderFiltersState;
      setFiltersState: Dispatch<SetStateAction<LenderFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'delivery_queued';
      filtersState: DeliveryQueuedFiltersState;
      setFiltersState: Dispatch<SetStateAction<DeliveryQueuedFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'price_locked';
      filtersState: PriceLockedFiltersState;
      setFiltersState: Dispatch<SetStateAction<PriceLockedFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'traded_in';
      filtersState: TradeInFiltersState;
      setFiltersState: Dispatch<SetStateAction<TradeInFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'delivery_date';
      filtersState: DeliveryDateFiltersState;
      setFiltersState: Dispatch<SetStateAction<DeliveryDateFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'pickup_date';
      filtersState: PickupDateFiltersState;
      setFiltersState: Dispatch<SetStateAction<PickupDateFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'last_updated_date';
      filtersState: LastUpdatedDateFiltersState;
      setFiltersState: Dispatch<SetStateAction<LastUpdatedDateFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    }
  | {
      type: 'created_date';
      filtersState: CreatedDateFiltersState;
      setFiltersState: Dispatch<SetStateAction<CreatedDateFiltersState>>;
      deleteFilter: () => void;
      shouldDeleteWhenNoFiltersAreSelected: boolean;
    };

export type SalesRepFilterChipProps = {
  filtersState: SalesRepFiltersState;
  setFiltersState: Dispatch<SetStateAction<SalesRepFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type MatchPreference = 'match-all-filters' | 'match-any-filter';

export type DeliveryQueuedFiltersState = {
  filterMetaState: FilterMetaState & {
    type: 'delivery_queued';
  };
  selectedFilters: {
    queued: boolean;
  };
};

export type PriceLockedFiltersState = {
  filterMetaState: FilterMetaState & {
    type: 'price_locked';
  };
  selectedFilters: {
    locked: boolean;
  };
};

export type DeliveryQueuedFilterChipProps = {
  filtersState: DeliveryQueuedFiltersState;
  setFiltersState: Dispatch<SetStateAction<DeliveryQueuedFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type PriceLockedFilterChipProps = {
  filtersState: PriceLockedFiltersState;
  setFiltersState: Dispatch<SetStateAction<PriceLockedFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type LenderFilters = Record<Lender['id'], boolean>;

export type LenderFiltersState = {
  selectedFilters: LenderFilters;
  filterMetaState: FilterMetaState & {
    type: 'lender';
  };
};

export type LenderFilterChipProps = {
  filtersState: LenderFiltersState;
  setFiltersState: Dispatch<SetStateAction<LenderFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type TradeInFiltersState = {
  filterMetaState: FilterMetaState & {
    type: 'traded_in';
  };
  selectedFilters: {
    tradedIn: boolean;
  };
};

export type TradeInFilterChipProps = {
  filtersState: TradeInFiltersState;
  setFiltersState: Dispatch<SetStateAction<TradeInFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type DeliveryDateFilterOptions =
  | 'none'
  | 'in-the-past'
  | '1-day-from-now'
  | '3-days-from-now'
  | '5-days-from-now'
  | '7-days-from-now'
  | '14-days-from-now';

export type PickupDateFilterOptions =
  | 'none'
  | 'in-the-past'
  | '1-day-from-now'
  | '3-days-from-now'
  | '5-days-from-now'
  | '7-days-from-now'
  | '14-days-from-now';

export type LastUpdatedDateFilterOptions =
  | 'none'
  | '1-day-ago'
  | '3-days-ago'
  | '1-week-ago'
  | '1-month-ago'
  | '3-months-ago'
  | '6-months-ago'
  | '1-year-ago';

export const DELIVERY_DATE_FILTER_OPTIONS: {
  label: string;
  value: DeliveryDateFilterOptions;
}[] = [
  {
    label: 'In the past',
    value: 'in-the-past',
  },
  {
    label: '1 day from now',
    value: '1-day-from-now',
  },
  {
    label: '3 days from now',
    value: '3-days-from-now',
  },
  {
    label: '5 days from now',
    value: '5-days-from-now',
  },
  {
    label: '7 days from now',
    value: '7-days-from-now',
  },
  {
    label: '14 days from now',
    value: '14-days-from-now',
  },
];

export type DeliveryDateFiltersState = {
  selectedFilters: DeliveryDateFilterOptions;
  filterMetaState: FilterMetaState & {
    type: 'delivery_date';
  };
};

export type DeliveryDateFilterChipProps = {
  filtersState: DeliveryDateFiltersState;
  setFiltersState: Dispatch<SetStateAction<DeliveryDateFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export const PICKUP_DATE_FILTER_OPTIONS: {
  label: string;
  value: PickupDateFilterOptions;
}[] = [
  {
    label: 'In the past',
    value: 'in-the-past',
  },
  {
    label: '1 day from now',
    value: '1-day-from-now',
  },
  {
    label: '3 days from now',
    value: '3-days-from-now',
  },
  {
    label: '5 days from now',
    value: '5-days-from-now',
  },
  {
    label: '7 days from now',
    value: '7-days-from-now',
  },
  {
    label: '14 days from now',
    value: '14-days-from-now',
  },
];
export type PickupDateFiltersState = {
  selectedFilters: PickupDateFilterOptions;
  filterMetaState: FilterMetaState & {
    type: 'pickup_date';
  };
};

export type PickupDateFilterChipProps = {
  filtersState: PickupDateFiltersState;
  setFiltersState: Dispatch<SetStateAction<PickupDateFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export const LAST_UPDATED_DATE_FILTER_OPTIONS: {
  label: string;
  value: LastUpdatedDateFilterOptions;
}[] = [
  {
    label: '1 day ago',
    value: '1-day-ago',
  },
  {
    label: '3 days ago',
    value: '3-days-ago',
  },
  {
    label: '1 week ago',
    value: '1-week-ago',
  },
  {
    label: '1 month ago',
    value: '1-month-ago',
  },
  {
    label: '3 months ago',
    value: '3-months-ago',
  },
  {
    label: '6 months ago',
    value: '6-months-ago',
  },
  {
    label: '1 year ago',
    value: '1-year-ago',
  },
];

export type LastUpdatedDateFiltersState = {
  selectedFilters: LastUpdatedDateFilterOptions;
  filterMetaState: FilterMetaState & {
    type: 'last_updated_date';
  };
};

export type LastUpdatedDateFilterChipProps = {
  filtersState: LastUpdatedDateFiltersState;
  setFiltersState: Dispatch<SetStateAction<LastUpdatedDateFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};

export type CreatedDateFilterOptions =
  | 'none'
  | '1-day-ago'
  | '3-days-ago'
  | '1-week-ago'
  | '1-month-ago'
  | '3-months-ago'
  | '6-months-ago'
  | '1-year-ago';

export const CREATED_DATE_FILTER_OPTIONS: {
  label: string;
  value: CreatedDateFilterOptions;
}[] = [
  {
    label: '1 day ago',
    value: '1-day-ago',
  },
  {
    label: '3 days ago',
    value: '3-days-ago',
  },
  {
    label: '1 week ago',
    value: '1-week-ago',
  },
  {
    label: '1 month ago',
    value: '1-month-ago',
  },
  {
    label: '3 months ago',
    value: '3-months-ago',
  },
  {
    label: '6 months ago',
    value: '6-months-ago',
  },
  {
    label: '1 year ago',
    value: '1-year-ago',
  },
];

export type CreatedDateFiltersState = {
  selectedFilters: CreatedDateFilterOptions;
  filterMetaState: FilterMetaState & {
    type: 'created_date';
  };
};

export type CreatedDateFilterChipProps = {
  filtersState: CreatedDateFiltersState;
  setFiltersState: Dispatch<SetStateAction<CreatedDateFiltersState>>;
  deleteFilter: () => void;
  shouldDeleteWhenNoFiltersAreSelected: boolean;
};
